import { Editor } from '@tiptap/core';
import { Fragment, Node as PMNode, ResolvedPos } from '@tiptap/pm/model';
import { TextSelection } from '@tiptap/pm/state';

// Imperative helpers for the multi-column layout. They operate on explicit
// positions (from a column NodeView's getPos) rather than the selection, which
// is the robust way to drive per-column toolbar buttons. Width is a fraction
// (0..1) per column; the set always sums to 1. The "≥2 columns or unwrap" rule
// lives here (the schema uses `column+`, per ProseMirror #782) .

const MIN_FRACTION = 0.04; // ~2–3 characters at typical content widths

interface ResolvedColumn {
  row: PMNode;
  rowPos: number;
  colIndex: number;
  $col: ResolvedPos;
}

// columnPos = the position directly before a `column` node (what getPos returns).
function resolveColumn(editor: Editor, columnPos: number): ResolvedColumn | null {
  const { doc } = editor.state;
  if (columnPos < 0 || columnPos > doc.content.size) return null;
  const $col = doc.resolve(columnPos);
  const row = $col.parent;
  if (row.type.name !== 'columns') return null;
  return { row, rowPos: $col.before(), colIndex: $col.index(), $col };
}

function colWidth(node: PMNode, fallback: number): number {
  return typeof node.attrs.width === 'number' && node.attrs.width > 0 ? node.attrs.width : fallback;
}

// Start position of the column at `index` inside a row that begins at rowPos.
function columnStart(row: PMNode, rowPos: number, index: number): number {
  let pos = rowPos + 1;
  for (let i = 0; i < index; i++) pos += row.child(i).nodeSize;
  return pos;
}

// Insert an N-column row at the selection. A plain current block (paragraph /
// heading) is moved into the first column; otherwise empty columns are inserted.
export function insertColumns(editor: Editor, n = 2): boolean {
  const { state } = editor;
  const { schema } = state;
  const columnType = schema.nodes.column;
  const columnsType = schema.nodes.columns;
  const paragraphType = schema.nodes.paragraph;
  if (!columnType || !columnsType || !paragraphType) return false;

  const { $from } = state.selection;
  let from: number | null = null;
  let to: number | null = null;
  let currentBlock: PMNode | null = null;
  try {
    from = $from.before(1);
    to = $from.after(1);
    currentBlock = state.doc.nodeAt(from);
  } catch {
    currentBlock = null;
  }
  // Don't wrap an existing columns block into a column.
  const wrap = !!currentBlock && currentBlock.isTextblock;

  const width = 1 / n;
  const cols: PMNode[] = [];
  for (let i = 0; i < n; i++) {
    const body = i === 0 && wrap && currentBlock ? Fragment.from(currentBlock) : Fragment.from(paragraphType.create());
    cols.push(columnType.create({ width }, body));
  }
  const columnsNode = columnsType.create(null, Fragment.fromArray(cols));

  const tr = state.tr;
  if (wrap && from != null && to != null) {
    tr.replaceWith(from, to, columnsNode);
    // Cursor into the first column's first child.
    const inside = tr.mapping.map(from) + 2;
    tr.setSelection(TextSelection.create(tr.doc, Math.min(inside, tr.doc.content.size)));
  } else {
    tr.replaceSelectionWith(columnsNode);
  }
  editor.view.dispatch(tr.scrollIntoView());
  editor.view.focus();
  return true;
}

// Append an empty column to the row, rebalancing widths so they still sum to 1.
export function addColumn(editor: Editor, columnPos: number): boolean {
  const found = resolveColumn(editor, columnPos);
  if (!found) return false;
  const { row, rowPos } = found;
  const { state } = editor;
  const columnType = state.schema.nodes.column;
  const paragraphType = state.schema.nodes.paragraph;

  const count = row.childCount + 1;
  const newWidth = 1 / count;
  const scale = 1 - newWidth;
  const tr = state.tr;

  let childPos = rowPos + 1;
  row.forEach((col) => {
    tr.setNodeMarkup(childPos, undefined, { ...col.attrs, width: colWidth(col, 1 / row.childCount) * scale });
    childPos += col.nodeSize;
  });
  const insertAt = rowPos + row.nodeSize - 1;
  tr.insert(insertAt, columnType.create({ width: newWidth }, paragraphType.create()));
  editor.view.dispatch(tr.scrollIntoView());
  return true;
}

// Delete a column; if that would leave fewer than two, unwrap the whole row back
// to plain blocks. Survivors are rebalanced to sum to 1.
export function deleteColumn(editor: Editor, columnPos: number): boolean {
  const found = resolveColumn(editor, columnPos);
  if (!found) return false;
  const { row, rowPos, colIndex } = found;
  const colNode = row.child(colIndex);
  const tr = editor.state.tr;

  if (row.childCount <= 2) {
    const blocks: PMNode[] = [];
    row.forEach((c) => c.forEach((child) => blocks.push(child)));
    tr.replaceWith(rowPos, rowPos + row.nodeSize, Fragment.fromArray(blocks));
    editor.view.dispatch(tr.scrollIntoView());
    return true;
  }

  const removedW = colWidth(colNode, 1 / row.childCount);
  const colStart = columnStart(row, rowPos, colIndex);
  tr.delete(colStart, colStart + colNode.nodeSize);

  const newRow = tr.doc.nodeAt(rowPos);
  if (newRow) {
    let p = rowPos + 1;
    newRow.forEach((c) => {
      tr.setNodeMarkup(p, undefined, { ...c.attrs, width: colWidth(c, 1 / newRow.childCount) / (1 - removedW) });
      p += c.nodeSize;
    });
  }
  editor.view.dispatch(tr.scrollIntoView());
  return true;
}

// Move a column one slot left (-1) or right (+1).
export function moveColumn(editor: Editor, columnPos: number, dir: -1 | 1): boolean {
  const found = resolveColumn(editor, columnPos);
  if (!found) return false;
  const { row, rowPos, colIndex } = found;
  const target = colIndex + dir;
  if (target < 0 || target >= row.childCount) return false;

  const col = row.child(colIndex);
  const colStart = columnStart(row, rowPos, colIndex);
  const tr = editor.state.tr;
  tr.delete(colStart, colStart + col.nodeSize);

  const newRow = tr.doc.nodeAt(rowPos);
  if (!newRow) return false;
  const insertPos = columnStart(newRow, rowPos, target);
  tr.insert(insertPos, col);
  editor.view.dispatch(tr.scrollIntoView());
  return true;
}

// Commit a divider drag: set the two adjacent columns' widths in one transaction.
export function commitResize(editor: Editor, leftPos: number, rightPos: number, lw: number, rw: number): void {
  const { state } = editor;
  const left = state.doc.nodeAt(leftPos);
  const right = state.doc.nodeAt(rightPos);
  if (!left || !right || left.type.name !== 'column' || right.type.name !== 'column') return;
  editor.view.dispatch(
    state.tr
      .setNodeMarkup(leftPos, undefined, { ...left.attrs, width: lw })
      .setNodeMarkup(rightPos, undefined, { ...right.attrs, width: rw }),
  );
}

export { MIN_FRACTION };
